import { Box, styled } from '@mui/material'
import { navigate } from 'gatsby'
import * as React from 'react'

import { actionDataState } from '../context/action-data-context'
import { resultsState } from '../context/results-context'

import { convertFiltersToParamObject, parameterizeFilters } from '../utilities/sanity-filters'
import { getDomain, isInBrowser } from '../utilities/browser'
import FullScreenModal from './full-screen-modal'
import ResultCard from './result-card'
import ResultsList from './result-list'

const ActionsList = ({ title, onPageChange }: { title?: string; onPageChange?: Function }) => {
  const { actions, totalActions, loading, nextPage, fetchNextPage } = actionDataState()

  const {
    filters,
    activeAction,
    showResultModal,
    modalWasClickedInto,
    setModalWasClickedInto,
    setActiveAction
  } = resultsState()

  const isExplorePage = React.useMemo(() => {
    if (!isInBrowser()) return false
    return window.location.pathname.split('/').filter(p => p)[0] === 'explore'
  }, [isInBrowser() ? window.location.pathname : ''])

  const actionUrlBase = isExplorePage ? '/explore/action/' : '/action/'
  const shareDomain = React.useMemo(() => getDomain() || '', [])

  const getPositions = React.useCallback((action: Queries.SanityAction) => {
    const anyAction = action as Queries.SanityAction & {
      position_ids?: unknown
    }
    if (Array.isArray(anyAction.position_ids)) {
      return anyAction.position_ids
    }
    return undefined
  }, [])

  const ensureActionWithPositions = React.useCallback(
    (action: Queries.SanityAction) => {
      const normalizedPositions = getPositions(action)
      if (normalizedPositions && normalizedPositions.length > 0) {
        return { ...action, position_ids: normalizedPositions } as Queries.SanityAction
      }
      return action
    },
    [getPositions]
  )

  const handleDetailClick = React.useCallback(
    (action: Queries.SanityAction) => {
      setModalWasClickedInto(true)
      setActiveAction(ensureActionWithPositions(action))
    },
    [setModalWasClickedInto, setActiveAction, ensureActionWithPositions]
  )

  const buildResultCardData = React.useCallback(
    (action: Queries.SanityAction) => {
      const {
        title,
        location,
        nouns,
        rank,
        description,
        descriptionShort,
        slug,
        categories,
        outreach,
        organization,
        tags,
        url
      } = action

      const actionUrl = `${actionUrlBase}${slug?.current}`
      return {
        title,
        location,
        nouns,
        rank,
        slug,
        categories,
        organization,
        outreach,
        tags,
        description: descriptionShort || description,
        url,
        position_ids: getPositions(action),
        actionUrl,
        shareUrl: `${shareDomain}${actionUrl}`
      }
    },
    [actionUrlBase, shareDomain, getPositions]
  )

  const renderActionCard = React.useCallback(
    (data: Queries.SanityAction) => {
      return (
        <ResultCard
          key={data.slug?.current}
          data={buildResultCardData(data)}
          consolidate
          footer="list"
          onDetailClick={() => handleDetailClick(data)}
        />
      )
    },
    [actionUrlBase, buildResultCardData, handleDetailClick]
  )

  const activeActionForModal = React.useMemo(() => {
    if (!activeAction) return undefined
    const normalizedActive = ensureActionWithPositions(activeAction)
    const existingPositions = getPositions(normalizedActive)
    if (existingPositions && existingPositions.length > 0) {
      return normalizedActive
    }

    const activeSlug = normalizedActive.slug?.current
    if (!activeSlug) return normalizedActive

    const match = actions.find(action => action.slug?.current === activeSlug)
    return match ? ensureActionWithPositions(match) : normalizedActive
  }, [activeAction, actions, ensureActionWithPositions, getPositions])

  return (
    <Container>
      <ResultsList
        title={title}
        results={actions}
        total={totalActions}
        loading={Boolean(loading)}
        nextPage={nextPage}
        fetchNextPage={fetchNextPage}
        onPageChange={onPageChange}
      >
        {renderActionCard}
      </ResultsList>

      <FullScreenModal
        open={Boolean(showResultModal)}
        colorScheme="white"
        allowInnerScroll
        onClose={() => {
          if (modalWasClickedInto) {
            navigate(-1)
          } else {
            // this is happening if user landed on a specific action
            navigate(
              !isExplorePage
                ? `/results/?${parameterizeFilters(convertFiltersToParamObject(filters))}`
                : '/explore'
            )
          }
        }}
      >
        {activeActionForModal && <ResultCard data={activeActionForModal} footer="detail" />}
      </FullScreenModal>
    </Container>
  )
}

const Container = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
`
export default ActionsList
